/**
 * Desktop encrypted API tunnel client-side wrapper.
 *
 * When running inside the Electron shell (window.electronAPI.platform === 'desktop'),
 * all fetch() calls targeting API URLs are transparently routed through the main-process
 * encrypted IPC tunnel. No tokens are stored or exposed in the renderer.
 */

declare global {
  interface Window {
    electronAPI?: {
      platform: string;
      data: {
        fetch: (endpoint: string, payload?: object) => Promise<unknown>;
      };
    };
  }
}

export function isDesktop(): boolean {
  return typeof window !== "undefined" && window.electronAPI?.platform === "desktop";
}

/**
 * Build a Response-like object from the decrypted JSON payload returned by the
 * desktop main-process encrypted tunnel, so existing .json() / .ok / .status
 * chains continue to work without any changes.
 */
function wrapDesktopResponse(
  status: number,
  payload: unknown
): Response {
  const body = JSON.stringify(payload);
  return new Response(body, {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

/**
 * Patch global fetch so that any request whose URL starts with the API base
 * is intercepted and routed through the encrypted desktop tunnel.
 *
 * This is applied once at app startup (see index.tsx). It is a no-op on web.
 */
export function patchGlobalFetchForDesktop(): void {
  if (!isDesktop()) return;
  if ((window as any).__desktopFetchPatched) return;
  (window as any).__desktopFetchPatched = true;

  const originalFetch = window.fetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === "string" ? input : input.toString();

    // Only intercept API calls (paths starting with /api/)
    // The desktop main process knows the full API_BASE; we pass only the path.
    const apiPathMatch = url.match(/^(?:https?:\/\/[^\/]+)?(\/api\/.*)$/);
    if (!apiPathMatch) {
      // Non-API fetch (e.g., static assets, third-party) — pass through unchanged.
      return originalFetch(input, init);
    }

    const endpoint = apiPathMatch[1];
    const method = init?.method ?? "GET";
    let payload: object | undefined;

    if (init?.body) {
      try {
        const bodyText =
          typeof init.body === "string"
            ? init.body
            : init.body instanceof Blob || init.body instanceof ArrayBuffer
            ? await new Response(init.body).text()
            : JSON.stringify(init.body);
        payload = JSON.parse(bodyText);
      } catch {
        payload = undefined;
      }
    }

    try {
      const result = await window.electronAPI!.data.fetch(endpoint, payload);
      return wrapDesktopResponse(200, result);
    } catch (err: any) {
      const message = err?.message || String(err);
      if (message.includes("NO_SESSION") || message.includes("SESSION_EXPIRED")) {
        // Return a 401-like response so the app can surface a "re-authenticate" prompt
        return wrapDesktopResponse(401, {
          success: false,
          error: "DESKTOP_SESSION_EXPIRED",
          message:
            "Session expired. Please re-authenticate in your browser and transfer a new session key.",
        });
      }
      return wrapDesktopResponse(500, {
        success: false,
        error: "DESKTOP_API_ERROR",
        message,
      });
    }
  };
}
